
import { defineComponent } from 'vue';
import { GoogleMapLoaderService } from '@/shared/services/GoogleMapLoaderService';
import { mapSettings, customMapSettings } from '@/shared/constants/mapSettings';
import { mapCountries } from '@/shared/constants/mapCountries';
import { Point } from '@/shared/models/Point';
import MapControls from '@/shared/components/MapControls.vue';

export default defineComponent({
  name: 'MapPositionReadonly',
  emits: ['initialized'],
  components: {
    MapControls,
  },
  props: {
    position: Object,
    country: {
      type: String,
      default: 'sk',
    },
    labelsEnabled: {
      type: Boolean,
      default: true,
    },
    streetViewControlEnabled: {
      type: Boolean,
      default: true,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    mapType: {
      type: String,
      default: "roadmap",
    },
  },
  data() {
    return {
      map: null as null | google.maps.Map,
      marker: null as null | google.maps.Marker,
      geocoder: null as null | google.maps.Geocoder,
    };
  },
  async mounted() {
    await GoogleMapLoaderService.load();
    this.initializeMap();
    this.$emit('initialized');
  },
  computed: {
    mapConfig() {
      const options = {
        ...mapSettings,
      } as google.maps.MapOptions;
      if (this.country) {
        const country = mapCountries[this.country as string];
        options.center = { lat: country.center_lat , lng: country.center_lng };
        options.zoom = country.zoom;
      }
      return options;
    },
  },
  watch: {
    position: function (newVal: null | Point) {
      if (newVal) {
        this.placeMarker(newVal);
      }
    },
  },
  methods: {
    initializeMap() {
      if(!window.google || !window.google.maps) {
        return;
      }
      const mapContainer = this.$refs.googleMap as HTMLElement;
      this.map = new window.google.maps.Map(mapContainer, this.mapConfig);
      this.map.setMapTypeId(this.mapType);
      this.geocoder = new window.google.maps.Geocoder();
      this.marker = new window.google.maps.Marker({
        map: this.map,
        draggable: false,
        icon: {
          url: customMapSettings.markerIconUrl,
          scaledSize: new window.google.maps.Size(customMapSettings.markerIconSize, customMapSettings.markerIconSize),
        },
      });
      if (this.position) {
        this.placeMarker(this.position);
      }
    },
    placeMarker(position: any) {
      if (this.marker) {
        this.marker.setPosition(position);
        this.markerChanged(position);
      }
    },
    markerChanged(position: any) {
      if (this.geocoder) {
        this.geocoder.geocode({ location: position }, (results, status) => {
          if (status === 'OK') {
            if (results.length > 0) {
              const place = results[0];
              this.setMapView(place, true);
            } else {
              console.error('No results found');
            }
          } else {
            console.error('Geocoder failed due to: ' + status);
          }
        });
      }
    },
    setMapView(place: google.maps.places.PlaceResult | google.maps.GeocoderResult, markerCenter?: boolean) {
      if (!this.map) return;

      if (place && place.geometry) {
        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport);
        } else {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(12);
        }
      }

      if (markerCenter && this.marker) {
        this.map.panTo((this.marker as any).getPosition());
      }
    },
    setMapType(value: string) {
      this.map.setMapTypeId(value);
    }
  },
});
